import { AppMessagesContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { useContext, useState, useRef, useMemo, Fragment, ReactNode } from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassButton } from "src/components/GlassButtons";
import { GlassInfo } from "src/components/MitGlassComponents";
import { BsChevronDoubleDown, BsChevronDoubleUp } from "react-icons/bs";
import { GlassInputControl } from "src/components/GlassInput";

export type ActionSpec = { title: string, handler: () => void };

export type ListItemSpec = {
    path: string,
    size: number,
    contentType: string,
    icon: any,
    [Metadata: string]: any
    actions: ActionSpec[],
}

export const PersistedFileListView = (props: {
    title: string,
    keysToDisplayWithHeaders: { [key: string]: string },
    sortField?: string,
    callBackOnAction?: (action: string, parameters: any) => void,
    listItems: any[],
    persistFile(fileNameExtension: string, fileName: string, extension: string, currentFile: File): Promise<void>,
    uploadAvailable?: boolean,
    linksAvailable?: boolean,
    persistLink?: (url: string, displayName?: string) => Promise<void>,
    toolbarContent?: ReactNode,
}) => {
    const { dispatch: appMessagesDispatch } = useContext(AppMessagesContext);

    async function loadFiles(evt: any) {
        try {
            let currentFile: File;
            let fileSource: FileList;
            if (evt.dataTransfer) {
                fileSource = evt.dataTransfer.files;
            } else {
                if (evt.target) {
                    fileSource = evt.target.files;
                } else {
                    fileSource = evt.files;
                }
            }
            // todo: warning and user accept when importing multiple...
            let promises = Object.keys(fileSource).map(async (elem: any) => {
                currentFile = fileSource[elem];
                if (currentFile) {
                    return await loadOneFile(currentFile);
                } else {
                    return undefined;
                    // Ignore dropping of other objects than files.
                }
            });

            const itemList = await Promise.all(promises);
            let count = itemList.length;
            appMessagesDispatch(actionSetInfoMessage(Localization.getFormattedText("Loaded {count} files", { count: count })));
        } catch (error: any) {
            appMessagesDispatch(actionSetErrorMessage("Error loading file: " + (error.message || error)));
        }
    }

    async function loadOneFile(currentFile: File) {
        console.log(currentFile.name);
        let pathfilename = currentFile.name;
        let filenameextension = pathfilename.replace(/^.*[\\/]/, '');
        let filename = filenameextension.substring(0, filenameextension.lastIndexOf('.'));
        let ext = filenameextension.split('.').pop()!.toLowerCase();
        props.persistFile(filenameextension, filename, ext, currentFile)
            .catch(error => {
                appMessagesDispatch(actionSetErrorMessage(error));
            });
    }

    return (
        <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "5px" }}>
            <DropTargetTitle
                title={props.title}
                callBackWhenDropOrUpload={(event) => { loadFiles(event); }}
                dropEnabled={Boolean(props.uploadAvailable)}
            />
            {props.toolbarContent ? (
                <div style={{ margin: "10px 0" }}>
                    {props.toolbarContent}
                </div>
            ) : null}
            <FileTable
                headers={props.keysToDisplayWithHeaders}
                objects={props.listItems}
                sortField={props.sortField}
                linksAvailable={props.linksAvailable}
                persistLink={props.persistLink}
            />
        </div>
    )
}

function DropTargetTitle(props: { title: string; callBackWhenDropOrUpload: (evt: any, dropped: boolean) => void; dropEnabled: boolean; }) {
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const myFileInput = useRef(null);

    // This function will be triggered when dropping
    const dropHandler = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        props.callBackWhenDropOrUpload(e, true);
    };

    const uploadFileHandler = (evt: any) => {
        props.callBackWhenDropOrUpload(evt, false);
    }

    function onClickAdd(e: any) {
        e.preventDefault();
        (myFileInput.current! as HTMLElement).click();
    }

    return (
        <>
            <GlassInfo
                className={"DropTargetTitle" + (props.dropEnabled ? " dropEnabled" : " dropDisabled")}
                style={{ border: isDragging ? "3px dashed darkorange" : (props.dropEnabled ? "3px dashed darkslategrey" : ""), cursor: props.dropEnabled ? "pointer" : "default" }}
                onDragEnter={() => { if (props.dropEnabled) setIsDragging(true) }}
                onDragOver={(event: any) => {
                    if (props.dropEnabled) event.preventDefault();
                }}
                onClick={(e) => { if (props.dropEnabled) onClickAdd(e) }}
                onDrop={e => { if (props.dropEnabled) dropHandler(e) }}
                onDragLeave={() => { if (props.dropEnabled) setIsDragging(false) }}
            >
                <h4>{Localization.getText(props.title)}</h4>
                <div>{props.dropEnabled ? (isDragging ? Localization.getText("Release to upload file(s)") : Localization.getText("Click or drop file(s) here to upload")) : ""}</div>
            </GlassInfo>
            {/* Hidden elements to handle file upload */}
            <div style={{ display: "none" }} id="excel">
                <input ref={myFileInput} multiple={true} type="file" id="files" name="files[]" onChange={uploadFileHandler} />
                <output id="list" />
            </div>
        </>
    );
}

function FileTable(props: {
    headers: { [key: string]: string },
    objects: { [key: string]: any }[],
    sortField?: string,
    linksAvailable?: boolean,
    persistLink?: (url: string, displayName?: string) => Promise<void>,
}) {
    let [sortFieldKey, setSortFieldKey] = useState(props.sortField);
    let [sortDirectionReverse, setSortDirectionReverse] = useState<boolean>(false);
    const headers = props.headers;
    const headL = Object.keys(headers).length;
    const [linkInputValue, setLinkInputValue] = useState<string>("");
    const [linkNameValue, setLinkNameValue] = useState<string>("");

    const objects = useMemo(() => {
        return [...props.objects].sort(FileRowSort(sortFieldKey, sortDirectionReverse));
    }, [props.objects, sortFieldKey, sortDirectionReverse]);


    const submitLink = async () => {
        if (!linkInputValue.trim() || !props.persistLink) {
            return;
        }
        try {
            await props.persistLink(linkInputValue.trim(), linkNameValue.trim() || undefined);
            setLinkInputValue("");
            setLinkNameValue("");
        } catch (error: any) {
            console.warn("Failed to persist link", error);
            // error already surfaced in persistLink
        }
    };

    const handleLinkKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            await submitLink();
        }
    };

    return (
        <div className="FileTable" style={{ position: "relative", display: "grid", margin: "10px", gridTemplateColumns: `repeat(${headL}, auto)`, maxHeight: "100%", overflow: "auto" }} >
            {Object.entries(headers).map(([key, value]) => (
                <div key={key} className="FileTableHeader"
                    style={{ cursor: "pointer", position: "sticky", zIndex: "1", top: 0, borderBottom: "1px solid lightgray", marginBottom: "5px", background: "white", fontWeight: (key === sortFieldKey ? "800" : "normal") }}
                    onClick={() => {
                        if (key !== sortFieldKey) {
                            setSortFieldKey(key);
                            return;
                        }
                        setSortDirectionReverse(!sortDirectionReverse);
                    }}
                >
                    {key === sortFieldKey ? (sortDirectionReverse ? <BsChevronDoubleDown /> : <BsChevronDoubleUp />) : ""}
                    <span style={{ marginLeft: "5px" }}>
                        {Localization.getText(value)}
                    </span>
                </div>
            ))}
            {objects.map((obj, objIndex) => Object.entries(headers).map(([key, _value]) => (
                <Fragment key={`${obj.path || objIndex}-${key}`}>
                    <div className={`file-table__cell ${key === "filename" ? "file-table__cell--filename" : ""}`}>
                        {PropertyFormatter(key, obj[key], obj.contentType, obj)}
                    </div>
                </Fragment>
            )))}
            {props.linksAvailable && props.persistLink && (
                <div style={{ gridColumn: "1 / -1", paddingInline: "5px", marginBottom: "5px", marginTop: "5px", paddingTop: "5px", borderTop: "1px solid #eee" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr auto", gap: "8px", alignItems: "center" }}>
                        <GlassInputControl>
                            <input
                                type="text"
                                placeholder={Localization.getText("Enter link URL")}
                                value={linkInputValue}
                                onChange={(e) => setLinkInputValue(e.target.value)}
                                onKeyDown={handleLinkKeyDown}

                            />
                        </GlassInputControl>
                        <GlassInputControl>
                            <input
                                type="text"
                                placeholder={Localization.getText("Optional display name")}
                                value={linkNameValue}
                                onChange={(e) => setLinkNameValue(e.target.value)}
                                onKeyDown={handleLinkKeyDown}

                            />
                        </GlassInputControl>
                        <GlassButton disabled={!linkInputValue.trim()} onClick={submitLink}>
                            {Localization.getText("Save link")}
                        </GlassButton>
                    </div>
                </div>
            )}
        </div>
    )
}

function FileRowSort(sortFieldKey?: string, sortDirectionReverse?: boolean) {
    return (a: { [key: string]: string }, b: { [key: string]: string }) => {
        if (sortFieldKey && a[sortFieldKey] && b[sortFieldKey]) {
            let valA: any = a[sortFieldKey];
            if (!Number.isNaN(Number.parseFloat(valA))) {
                valA = Number.parseFloat(valA);
            }
            let valB: any = b[sortFieldKey];
            if (!Number.isNaN(Number.parseFloat(valB))) {
                valB = Number.parseFloat(valB);
            }
            if (valA < valB) {
                return sortDirectionReverse ? 1 : -1;
            }
            else {
                return sortDirectionReverse ? -1 : 1;
            }
        } else {
            return 0; // no sorting
        }
    }
}

const DEFAULT_TEXT_MAX_LENGTH = 80;
const IMAGE_URL_REGEX = /\.(png|jpg|jpeg|gif|webp|svg)(\?|$)/i;

function truncateUrl(url: string, maxLength: number = DEFAULT_TEXT_MAX_LENGTH): string {
    if (url.length <= maxLength) {
        return url;
    }
    try {
        const urlObj = new URL(url);
        const domain = urlObj.origin;
        const path = urlObj.pathname + urlObj.search + urlObj.hash;

        if (domain.length + 10 > maxLength) {
            return url.substring(0, maxLength - 3) + "...";
        }

        const remainingLength = maxLength - domain.length - 3; // 3 for "..."
        if (path.length > remainingLength) {
            return domain + path.substring(0, remainingLength) + "...";
        }
        return url;
    } catch {
        return url.substring(0, maxLength - 3) + "...";
    }
}

function truncateText(value: string, maxLength: number = DEFAULT_TEXT_MAX_LENGTH) {
    if (value.length <= maxLength) {
        return value;
    }
    return value.substring(0, maxLength - 3) + "...";
}

function isImageUrl(url?: string) {
    return typeof url === "string" && url.startsWith("http") && IMAGE_URL_REGEX.test(url);
}

function renderFileLink(url: string, text: string, includePreview: boolean) {
    return (
        <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="file-table__link"
            title={url}
        >
            {includePreview && (
                <img
                    src={url}
                    className="file-table__imagePreview"
                    alt="Preview"
                />
            )}
            <span className="file-table__linkText">{text}</span>
        </a>
    );
}

function PropertyFormatter(headerName: string, value: any, contentType?: string, obj?: any) {
    if (!value) return "";

    switch (headerName) {
        case "icon": {
            if (contentType === "link") {
                return <span style={{ fontSize: "16px" }}>🔗</span>;
            }
            let blob = new Blob([new Uint8Array((value as Buffer))]);
            let url = URL.createObjectURL(blob);
            return (
                <img
                    style={{ height: "48px", maxHeight: "48px" }}
                    src={url}
                />
            )
        }
        case "filename": {
            const isLinkRow = obj?.contentType === "link";
            const stringValue = typeof value === "string" ? value : "";
            const linkUrl = isLinkRow
                ? obj?.linkUrl || (stringValue.startsWith("http") ? stringValue : undefined)
                : (stringValue.startsWith("http") ? stringValue : undefined);
            const displayName = obj?.linkDisplayName?.trim();

            if (linkUrl) {
                const linkText = displayName
                    ? truncateText(displayName)
                    : truncateUrl(linkUrl);
                const showPreview = isImageUrl(linkUrl);
                return renderFileLink(linkUrl, linkText, showPreview);
            }

            if (stringValue) {
                const truncated = truncateText(stringValue);
                return (
                    <span className="file-table__text" title={stringValue}>
                        {truncated}
                    </span>
                );
            }
            return value;
        }
        case "actions":
        case "open": {
            let actions = value as { title: string, handler: () => void }[];
            return actions.map((act, idx) => {
                return (
                    <GlassButton key={idx} style={{ marginLeft: idx !== 0 ? "3px" : "0" }} onClick={() => { act.handler(); }}>
                        {Localization.getText(act.title)}
                    </GlassButton>
                )
            });
        }
        case "lastmodified":
        case "timestamp": {
            return new Date(Number.parseInt(value)).toLocaleString();
        }
    }
    return value;
}